import { useMemo, useState } from 'react'

import {
  Alert,
  Badge,
  Box,
  Divider,
  Flex,
  Group,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import {
  IconAlertCircle,
  IconArrowNarrowLeft,
  IconExternalLink,
  IconMovie,
  IconPlayCardStar,
} from '@tabler/icons-react'
import { Link as RALink, useGetOne, useTranslate } from 'react-admin'
import {
  PromotionElementOutcomeApiDTO,
  PromotionOperationVideoAttributesApiDTO,
  PromotionOperationVideoStatusApiDTO,
} from '../../codegen/generated/Api.schemas'
import { useGetApiUsers } from '../../codegen/generated/users'
import { useGetApiVideoPromotionOperationsOperationId } from '../../codegen/generated/video-promotion'
import { DateHelper } from '../../helpers/Date.helper'
import { VideoPromotionHelper } from '../../helpers/VideoPromotion.helper'
import { YoutubeChannel } from '../../types/YoutubeChannel'
import { LXLoader } from '../LXLoader'
import { LXTable, LXTableColumn } from '../LXTable/LXTable'
import { PromotionConfigRecap } from './PromotionConfigRecap'

type LXOperationVideoTableType = {
  id: string
  videoTitle: string
  videoId: JSX.Element
  statusItem: JSX.Element
  processedAt: string
  errorDetail: JSX.Element
  actions: JSX.Element
}

const VIDEO_STATUS_COLORS: Record<PromotionOperationVideoStatusApiDTO, string> = {
  pending: 'gray',
  success: 'green',
  failed: 'red',
  skipped: 'orange',
}

const isSkippedOutcome = (outcome: PromotionElementOutcomeApiDTO) => outcome.startsWith('skipped_')

/** What happened to the endscreen and the infocards of a video, including why they were skipped */
const VideoOutcomes = ({ video }: { video: PromotionOperationVideoAttributesApiDTO }) => {
  const t = useTranslate()
  const outcomes = [
    { label: t('pages.videoPromotion.endScreen._title'), outcome: video.resultDetail?.endscreen },
    { label: t('pages.videoPromotion.infoCards._title'), outcome: video.resultDetail?.infocards },
  ].filter(
    (item): item is { label: string; outcome: PromotionElementOutcomeApiDTO } => !!item.outcome,
  )
  if (outcomes.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    )
  }
  return (
    <Stack gap={2}>
      {outcomes.map(({ label, outcome }) => (
        <Text key={label} size="sm" c={isSkippedOutcome(outcome) ? 'orange' : 'dimmed'}>
          {label} : {t(`pages.videoPromotion.detail.outcomes.${outcome}`)}
        </Text>
      ))}
    </Stack>
  )
}

export const VideoPromotionDetail = ({ operationId }: { operationId: string }) => {
  const t = useTranslate()

  const [statusFilter, setStatusFilter] = useState<'all' | PromotionOperationVideoStatusApiDTO>(
    'all',
  )
  const [search, setSearch] = useState('')

  const operationQuery = useGetApiVideoPromotionOperationsOperationId(operationId, {
    videoStatus: statusFilter === 'all' ? undefined : statusFilter,
    search: search || undefined,
  })
  const operation = operationQuery.data?.body.operation

  const usersQuery = useGetApiUsers()
  const youtubeChannelQuery = useGetOne<YoutubeChannel>(
    'youtubeChannels',
    {
      id: operation?.youtubeChannelId ?? '',
    },
    {
      enabled: !!operation?.youtubeChannelId,
    },
  )

  const createdByUserName = useMemo(() => {
    const user = (usersQuery.data?.body ?? []).find(u => u.id === operation?.createdByUserId)
    return user?.email ?? operation?.createdByUserId
  }, [usersQuery.data, operation?.createdByUserId])

  const endscreenSummary = useMemo(() => {
    const endscreen = operation?.config.endscreen
    if (!endscreen) {
      return undefined
    }
    if (endscreen.mode === 'copyFromVideo') {
      return t('pages.videoPromotion.confirmation.copyFromVideo', {
        videoId: endscreen.sourceVideoId,
      })
    }
    return t('pages.videoPromotion.detail.endscreenElements', {
      smart_count: endscreen.elements.length,
      startSecondsBeforeEnd: endscreen.startSecondsBeforeEnd,
    })
  }, [operation, t])

  const infocardsSummary = useMemo(() => {
    const infocards = operation?.config.infocards
    if (!infocards) {
      return undefined
    }
    if (infocards.mode === 'copyFromVideo') {
      return t('pages.videoPromotion.confirmation.copyFromVideo', {
        videoId: infocards.sourceVideoId,
      })
    }
    return t('pages.videoPromotion.confirmation.customInfocards', {
      smart_count: infocards.cards.length,
    })
  }, [operation, t])

  const columns = useMemo<LXTableColumn<LXOperationVideoTableType>[]>(
    () => [
      { key: 'videoTitle', value: t('pages.videoPromotion.videoName'), size: 'md' },
      {
        key: 'videoId',
        value: t('pages.videoPromotion.videoId'),
        type: 'component',
        size: 'sm',
      },
      {
        key: 'statusItem',
        value: t('_generics.status'),
        type: 'component',
        size: 'sm',
      },
      {
        key: 'processedAt',
        value: t('pages.videoPromotion.detail.processedAt'),
        size: 'sm',
      },
      {
        key: 'errorDetail',
        value: t('pages.videoPromotion.detail.errorDetail'),
        type: 'component',
      },
      { key: 'actions', value: '', type: 'component', size: 'xs' },
    ],
    [t],
  )

  const items = useMemo<LXOperationVideoTableType[]>(
    () =>
      (operationQuery.data?.body.videos ?? []).map(
        (video: PromotionOperationVideoAttributesApiDTO) => ({
          id: video.id,
          videoTitle: video.videoTitle ?? '—',
          videoId: <Text size="sm">{video.videoId}</Text>,
          statusItem: (
            <Badge color={VIDEO_STATUS_COLORS[video.status]} variant="light">
              {t(`pages.videoPromotion.statuses.${video.status}`)}
            </Badge>
          ),
          processedAt: video.processedAt
            ? DateHelper.format(video.processedAt, 'DD-MM-YYYY HH:mm:ss')
            : '—',
          errorDetail: video.errorMessage ? (
            <Text size="sm" c="red">
              {video.errorMessage}
            </Text>
          ) : (
            <VideoOutcomes video={video} />
          ),
          actions: (
            <a
              href={`https://youtu.be/${video.videoId}`}
              target="_blank"
              rel="noreferrer"
              aria-label={t('pages.videoPromotion.detail.seeOnYoutube')}
            >
              <IconExternalLink size={16} />
            </a>
          ),
        }),
      ),
    [operationQuery.data, t],
  )

  if (!operation) {
    return null
  }

  const status = VideoPromotionHelper.getStatus(operation.status)

  return (
    <Stack>
      <RALink to="/videosPromotions">
        <Group gap={4} wrap="nowrap">
          <IconArrowNarrowLeft size={16} />
          <Text size="sm">{t('_generics.backToList')}</Text>
        </Group>
      </RALink>
      {operation.isSimulated && (
        <Alert
          color="blue"
          variant="light"
          icon={<IconAlertCircle size={20} />}
          title={t('pages.videoPromotion.detail.simulated')}
        />
      )}

      <Flex direction="row" justify="space-between" align="flex-start" wrap="wrap">
        <Group>
          <Title order={1} size="h2">
            {operation.name ?? operation.id}
          </Title>
          <Badge color={status.color} variant="light">
            {t(`pages.videoPromotion.statuses.${status.id}`)}
          </Badge>
        </Group>
        <Stack gap={2}>
          <Text size="sm" c="dimmed">
            {DateHelper.format(operation.createdAt, 'DD-MM-YYYY HH:mm')}
          </Text>
          <Text size="sm" c="dimmed">
            {t('pages.videoPromotion.createdBy')}: {createdByUserName ?? '—'}
          </Text>
          <Text size="sm" c="dimmed">
            {t('_generics.youtubeChannel')}:{' '}
            {youtubeChannelQuery.data?.name ?? operation.youtubeChannelId}
          </Text>
        </Stack>
      </Flex>

      <Stack gap="xs" mt="md">
        <Title order={5}>{t('pages.videoPromotion.confirmation.appliedElements')}</Title>
        <Group gap="xs">
          {!!operation.config.endscreen && (
            <Badge variant="default" leftSection={<IconMovie size={12} />}>
              <Box pt={3}>{t('pages.videoPromotion.endScreen._title')}</Box>
            </Badge>
          )}
          {!!operation.config.infocards && (
            <Badge variant="default" leftSection={<IconPlayCardStar size={12} />}>
              <Box pt={3}>{t('pages.videoPromotion.infoCards._title')}</Box>
            </Badge>
          )}
        </Group>

        <Divider w="80%" my="lg" mx="auto" />

        <Stack gap="xs">
          <Title order={5}>{t('pages.videoPromotion.confirmation.details')}</Title>
          {!!endscreenSummary && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                {t('pages.videoPromotion.endScreen._title')}
              </Text>
              <Text size="sm">{endscreenSummary}</Text>
            </Group>
          )}
          {!!infocardsSummary && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                {t('pages.videoPromotion.infoCards._title')}
              </Text>
              <Text size="sm">{infocardsSummary}</Text>
            </Group>
          )}
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              {t('pages.videoPromotion.confirmation.overwriteExistingEndscreen')}
            </Text>
            <Text size="sm">
              {operation.config.overwriteExistingEndscreen ? t('_generics.yes') : t('_generics.no')}
            </Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              {t('pages.videoPromotion.confirmation.overwriteExistingInfocards')}
            </Text>
            <Text size="sm">
              {operation.config.overwriteExistingInfocards ? t('_generics.yes') : t('_generics.no')}
            </Text>
          </Group>
        </Stack>
        <Box mt="md">
          <PromotionConfigRecap
            youtubeChannelId={operation.youtubeChannelId}
            endscreen={operation.config.endscreen}
            infocards={operation.config.infocards}
          />
        </Box>
      </Stack>
      <Group gap="sm" mx="auto">
        <Badge variant="light" color="blue">
          {t('pages.videoPromotion.confirmation.videos')}: {operation.videosCount}
        </Badge>
        <Badge variant="light" color="green">
          {t('pages.videoPromotion.statuses.success')}: {operation.successCount}
        </Badge>
        <Badge variant="light" color="red">
          {t('pages.videoPromotion.statuses.failed')}: {operation.failureCount}
        </Badge>
        <Badge variant="light" color="orange">
          {t('pages.videoPromotion.statuses.skipped')}: {operation.skippedCount}
        </Badge>
      </Group>

      <Divider w="80%" my="lg" mx="auto" />

      <Stack gap="xs">
        <Title order={5}>{t('pages.videoPromotion.detail.impactedVideos')}</Title>
        <Group justify="space-between" align="center" wrap="nowrap">
          <SegmentedControl
            value={statusFilter}
            onChange={value =>
              setStatusFilter(value as 'all' | PromotionOperationVideoStatusApiDTO)
            }
            color="blue"
            size="sm"
            // style={{ flex: 1 }}
            fullWidth
            data={[
              { value: 'all', label: t('pages.videoPromotion.detail.all') },
              { value: 'success', label: t('pages.videoPromotion.statuses.success') },
              { value: 'failed', label: t('pages.videoPromotion.statuses.failed') },
              { value: 'skipped', label: t('pages.videoPromotion.statuses.skipped') },
            ]}
          />
          <Group justify="right" align="center" gap="xs">
            <TextInput
              placeholder={t('_generics.search')}
              value={search}
              onChange={event => setSearch(event.currentTarget.value)}
              w={220}
            />
          </Group>
        </Group>
        <Stack mih={150}>
          {operationQuery.isLoading ? (
            <LXLoader size="md" />
          ) : (
            <LXTable items={items} columns={columns} />
          )}
        </Stack>
      </Stack>
    </Stack>
  )
}
